"use client";

import { c as _c } from "react/compiler-runtime";
import { IconArrowUpRightOutline18, IconCheckOutline18, IconChevronDownOutline18, IconCopyOutline18 } from "nucleo-ui-outline-18";
import { useEffect, useRef, useState } from "react";
import { PixelLoader } from "@/components/ui/pixel-loader";
import type { TriggerArchetypeProps } from "./types";
import TokenChainAmountInput from "../../form-fields/TokenChainAmountInput";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import JsonInput from "../../form-fields/JsonInput";
import { useTriggerInfo } from "@/hooks/useTriggerInfo";
import { useSimTokenInfo } from "@/hooks/useSimTokenInfo";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useOrganizations } from "@/hooks/useOrganizations";
import { rawToDecimal } from "../../TriggerInputModal";
import { AnySpendWorkflowTrigger } from "@b3dotfun/sdk/anyspend/react";
const DEFAULT_CHAIN_ID = 8453;
const DEFAULT_TOKEN_ADDRESS = "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913";
const DEFAULT_TOKEN_SYMBOL = "USDC";
const DEFAULT_TOKEN_DECIMALS = 6;
function DraftMode(t0) {
  const $ = _c(39);
  const {
    triggerPayload,
    onRun
  } = t0;
  const configuredChainId = triggerPayload?.chainId as number || DEFAULT_CHAIN_ID;
  const configuredTokenAddress = triggerPayload?.tokenAddress as string || DEFAULT_TOKEN_ADDRESS;
  const configuredAmount = triggerPayload?.amount as string | undefined;
  const configuredRecipientAddress = triggerPayload?.recipientAddress as string || "";
  const [chainId, setChainId] = useState(configuredChainId);
  const [tokenAddress, setTokenAddress] = useState(configuredTokenAddress);
  const [inputsJson, setInputsJson] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const isDefaultToken = tokenAddress === DEFAULT_TOKEN_ADDRESS;
  const {
    data: tokenInfo
  } = useSimTokenInfo(tokenAddress, chainId, !isDefaultToken);
  const tokenMeta = tokenInfo?.tokens[0];
  const tokenSymbol = isDefaultToken ? DEFAULT_TOKEN_SYMBOL : tokenMeta?.symbol ?? "TOKEN";
  const tokenDecimals = isDefaultToken ? DEFAULT_TOKEN_DECIMALS : tokenMeta?.decimals ?? 18;
  const [amount, setAmount] = useState(configuredAmount ?? "");
  const [stableIds] = useState(_temp);
  const onRunRef = useRef(onRun);
  let t1;
  if ($[0] !== onRun) {
    t1 = () => {
      onRunRef.current = onRun;
    };
    $[0] = onRun;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useEffect(t1);
  let t2;
  if ($[2] !== amount || $[3] !== chainId || $[4] !== configuredRecipientAddress || $[5] !== inputsJson || $[6] !== stableIds.orderId || $[7] !== stableIds.triggeredAt || $[8] !== stableIds.txHash || $[9] !== tokenAddress || $[10] !== tokenDecimals || $[11] !== tokenSymbol) {
    t2 = () => {
      let parsedInputs = {};
      if (inputsJson.trim()) {
        try {
          parsedInputs = JSON.parse(inputsJson);
        } catch {}
      }
      onRunRef.current({
        triggeredAt: stableIds.triggeredAt,
        payment: {
          txHash: stableIds.txHash,
          orderId: stableIds.orderId,
          status: "executed",
          dstChain: chainId,
          dstTokenAddress: tokenAddress,
          dstTokenSymbol: tokenSymbol,
          dstTokenDecimals: tokenDecimals,
          recipientAddress: configuredRecipientAddress,
          dstAmount: amount || "0",
          dstAmountFormatted: amount ? rawToDecimal(amount, tokenDecimals) : "0"
        },
        inputs: parsedInputs
      });
    };
    $[2] = amount;
    $[3] = chainId;
    $[4] = configuredRecipientAddress;
    $[5] = inputsJson;
    $[6] = stableIds.orderId;
    $[7] = stableIds.triggeredAt;
    $[8] = stableIds.txHash;
    $[9] = tokenAddress;
    $[10] = tokenDecimals;
    $[11] = tokenSymbol;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== amount || $[14] !== chainId || $[15] !== configuredRecipientAddress || $[16] !== inputsJson || $[17] !== stableIds || $[18] !== tokenAddress || $[19] !== tokenDecimals || $[20] !== tokenSymbol) {
    t3 = [amount, chainId, tokenAddress, inputsJson, tokenSymbol, tokenDecimals, configuredRecipientAddress, stableIds];
    $[13] = amount;
    $[14] = chainId;
    $[15] = configuredRecipientAddress;
    $[16] = inputsJson;
    $[17] = stableIds;
    $[18] = tokenAddress;
    $[19] = tokenDecimals;
    $[20] = tokenSymbol;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  useEffect(t2, t3);
  let t4;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (addr, chain, _symbol, _decimals) => {
      setTokenAddress(addr);
      setChainId(chain);
      setAmount("");
    };
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== amount || $[24] !== chainId || $[25] !== tokenAddress) {
    t5 = <TokenChainAmountInput label="Amount" amount={amount} onAmountChange={setAmount} tokenAddress={tokenAddress} chainId={chainId} onTokenChange={t4} popoverModal={true} />;
    $[23] = amount;
    $[24] = chainId;
    $[25] = tokenAddress;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setShowAdvanced(_temp2);
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  const t7 = `h-3.5 w-3.5 transition-transform ${showAdvanced ? "rotate-180" : "rotate-0"}`;
  let t8;
  if ($[28] !== t7) {
    t8 = <button type="button" onClick={t6} className="label label-xs hover:text-ds-text-secondary flex items-center gap-1.5 transition-colors dark:hover:text-zinc-300"><IconChevronDownOutline18 className={t7} />Advanced (payload)</button>;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== inputsJson || $[31] !== showAdvanced) {
    t9 = showAdvanced && <div className="mt-2"><JsonInput label="" value={inputsJson} onChange={setInputsJson} placeholder={"{\"key\": \"value\"}"} /><p className="text-ds-text-disabled mt-1 text-xs">{"Custom data passed as trigger result inputs (accessible via {{root.result.inputs.*}})"}</p></div>;
    $[30] = inputsJson;
    $[31] = showAdvanced;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== t8 || $[34] !== t9) {
    t10 = <div>{t8}{t9}</div>;
    $[33] = t8;
    $[34] = t9;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== t10 || $[37] !== t5) {
    t11 = <div className="space-y-4">{t5}{t10}</div>;
    $[36] = t10;
    $[37] = t5;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  return t11;
}
function _temp2(prev) {
  return !prev;
}
function _temp() {
  return {
    txHash: "0x" + crypto.randomUUID().replace(/-/g, "") + crypto.randomUUID().replace(/-/g, ""),
    orderId: "test_" + crypto.randomUUID().replace(/-/g, "").slice(0, 16),
    triggeredAt: new Date().toISOString()
  };
}
function LivePaymentLink(t0) {
  const $ = _c(31);
  const {
    workflowId
  } = t0;
  const {
    triggerInfo,
    isLoading
  } = useTriggerInfo(workflowId, true);
  const {
    isCopied,
    copyToClipboard
  } = useCopyToClipboard();
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex items-center gap-2 py-4"><PixelLoader size="sm" color="blue" /><span className="text-ds-text-muted text-sm">Loading payment link...</span></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (!triggerInfo?.paymentLinkUrl) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p className="body-sm text-ds-text-disabled dark:text-ds-text-muted py-4">Publish the workflow to generate a payment link.</p>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <label className="field-label mb-1.5 block">Payment link</label>;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== triggerInfo.paymentLinkUrl) {
    t2 = <Input readOnly={true} value={triggerInfo.paymentLinkUrl} className="mono-xs dark:bg-ds-neutral-800 flex-1 truncate bg-zinc-50" />;
    $[3] = triggerInfo.paymentLinkUrl;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== copyToClipboard || $[6] !== triggerInfo.paymentLinkUrl) {
    t3 = () => copyToClipboard(triggerInfo.paymentLinkUrl);
    $[5] = copyToClipboard;
    $[6] = triggerInfo.paymentLinkUrl;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== isCopied) {
    t4 = isCopied ? <IconCheckOutline18 className="h-3.5 w-3.5 text-green-500" /> : <IconCopyOutline18 className="h-3.5 w-3.5" />;
    $[8] = isCopied;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = isCopied ? "Copied" : "Copy";
  let t6;
  if ($[10] !== t5) {
    t6 = <span className="text-xs">{t5}</span>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t6) {
    t7 = <Button variant="white" size="sm" onClick={t3} className="gap-1.5 px-3">{t4}{t6}</Button>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t2 || $[17] !== t7) {
    t8 = <div className="flex items-center gap-2">{t2}{t7}</div>;
    $[16] = t2;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <p className="text-ds-text-disabled mt-1.5 text-xs">Share this link to receive payments that trigger the workflow.</p>;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8) {
    t10 = <div>{t1}{t8}{t9}</div>;
    $[20] = t8;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== triggerInfo.paymentLinkUrl) {
    t11 = () => window.open(triggerInfo.paymentLinkUrl, "_blank", "noopener,noreferrer");
    $[22] = triggerInfo.paymentLinkUrl;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <IconArrowUpRightOutline18 className="h-4 w-4" />;
    t13 = <span className="text-sm">Open Checkout</span>;
    $[24] = t12;
    $[25] = t13;
  } else {
    t12 = $[24];
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t11) {
    t14 = <Button className="w-full" onClick={t11}>{t12}{t13}</Button>;
    $[26] = t11;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t10 || $[29] !== t14) {
    t15 = <div className="space-y-4">{t10}{t14}</div>;
    $[28] = t10;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}
function LiveDirectPayment(t0) {
  const $ = _c(11);
  const {
    triggerPayload,
    workflowId,
    onSuccess
  } = t0;
  const {
    selectedOrgId
  } = useOrganizations();
  const recipientAddress = triggerPayload?.recipientAddress as string || "";
  const chainId = triggerPayload?.chainId as number || 0;
  const tokenAddress = triggerPayload?.tokenAddress as string || "";
  const amount = triggerPayload?.amount as string || "0";
  if (!selectedOrgId) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p className="body-sm text-ds-text-disabled py-4">No organization selected.</p>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (!recipientAddress) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p className="body-sm text-ds-text-disabled py-4">Configure a recipient address in the trigger settings.</p>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="text-ds-text-muted text-xs">Complete the payment below to trigger the workflow with a real on-chain transaction.</p>;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== amount || $[4] !== chainId || $[5] !== onSuccess || $[6] !== recipientAddress || $[7] !== selectedOrgId || $[8] !== tokenAddress || $[9] !== workflowId) {
    t2 = <div className="space-y-4">{t1}<AnySpendWorkflowTrigger recipientAddress={recipientAddress} chainId={chainId} tokenAddress={tokenAddress} amount={amount} workflowId={workflowId} orgId={selectedOrgId} mode="page" onSuccess={onSuccess} callbackMetadata={undefined} onClose={undefined} actionLabel={undefined} classes={undefined} showFiatOption={undefined} /></div>;
    $[3] = amount;
    $[4] = chainId;
    $[5] = onSuccess;
    $[6] = recipientAddress;
    $[7] = selectedOrgId;
    $[8] = tokenAddress;
    $[9] = workflowId;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}

/** AnySpendPayment archetype — payment simulation form (draft) or real checkout (live). */
export function AnySpendPayment(t0) {
  const $ = _c(11);
  const {
    triggerPayload,
    onRun,
    isPublished,
    workflowId
  } = t0;
  const enablePaymentLink = triggerPayload?.enablePaymentLink === true;
  if (isPublished && workflowId) {
    if (enablePaymentLink) {
      let t1;
      if ($[0] !== workflowId) {
        t1 = <LivePaymentLink workflowId={workflowId} />;
        $[0] = workflowId;
        $[1] = t1;
      } else {
        t1 = $[1];
      }
      return t1;
    }
    let t1;
    if ($[2] !== onRun) {
      t1 = () => onRun({
        _livePaymentCompleted: true
      });
      $[2] = onRun;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== t1 || $[5] !== triggerPayload || $[6] !== workflowId) {
      t2 = <LiveDirectPayment triggerPayload={triggerPayload} workflowId={workflowId} onSuccess={t1} />;
      $[4] = t1;
      $[5] = triggerPayload;
      $[6] = workflowId;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t1;
  if ($[8] !== onRun || $[9] !== triggerPayload) {
    t1 = <DraftMode triggerPayload={triggerPayload} onRun={onRun} />;
    $[8] = onRun;
    $[9] = triggerPayload;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}